import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Building2,
  ChevronRight,
  Gauge,
  Info,
  KeyRound,
  MapPin,
  PieChart,
  ShieldCheck,
  Share2,
  Sparkles,
  Wallet,
} from 'lucide-react';
import {
  FacebookShareButton,
  TwitterShareButton,
  WhatsappShareButton,
  LinkedinShareButton,
  FacebookIcon,
  TwitterIcon,
  WhatsappIcon,
  LinkedinIcon,
} from 'react-share';

const counties = [
  'carlow',
  'cavan',
  'clare',
  'cork',
  'donegal',
  'dublin',
  'galway',
  'kerry',
  'kildare',
  'kilkenny',
  'laois',
  'leitrim',
  'limerick',
  'longford',
  'louth',
  'mayo',
  'meath',
  'monaghan',
  'offaly',
  'roscommon',
  'sligo',
  'tipperary',
  'waterford',
  'westmeath',
  'wexford',
  'wicklow',
];

const salaries = Array.from(
  { length: Math.floor((150000 - 25000) / 5000) + 1 },
  (_, index) => 25000 + index * 5000
);

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('en-IE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);

const toTitleCase = (value: string) =>
  value
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

const toPath = (salary: number, county: string) =>
  `/affordability/how-much-rent-can-i-afford-on-${salary}-in-${county}`;

const calculatePaye = (salary: number): number => {
  const standardBand = 44000;
  const taxCredits = 4000;
  const at20 = Math.min(salary, standardBand);
  const at40 = Math.max(salary - standardBand, 0);
  const grossTax = at20 * 0.2 + at40 * 0.4;
  return Math.max(Math.round(grossTax - taxCredits), 0);
};

const calculateUsc = (salary: number): number => {
  const bands = [
    { cap: 12012, rate: 0.005 },
    { cap: 15370, rate: 0.02 },
    { cap: 42662, rate: 0.03 },
    { cap: Infinity, rate: 0.08 },
  ];
  let remaining = salary;
  let usc = 0;
  for (const band of bands) {
    if (remaining <= 0) break;
    const taxable = Math.min(remaining, band.cap);
    usc += taxable * band.rate;
    remaining -= taxable;
  }
  return Math.round(usc);
};

const calculatePrsi = (salary: number): number => Math.round(salary * 0.04);

const RentAffordabilityDiscoverPage: React.FC = () => {
  const [selectedCounty, setSelectedCounty] = useState('dublin');
  const [selectedSalary, setSelectedSalary] = useState(50000);

  const featured = useMemo(
    () => [
      { county: 'dublin', salary: 35000 },
      { county: 'dublin', salary: 45000 },
      { county: 'dublin', salary: 60000 },
      { county: 'dublin', salary: 75000 },
      { county: 'cork', salary: 40000 },
      { county: 'cork', salary: 50000 },
      { county: 'cork', salary: 60000 },
      { county: 'galway', salary: 40000 },
      { county: 'galway', salary: 50000 },
      { county: 'limerick', salary: 40000 },
      { county: 'limerick', salary: 55000 },
      { county: 'waterford', salary: 40000 },
      { county: 'meath', salary: 45000 },
      { county: 'kildare', salary: 50000 },
      { county: 'kildare', salary: 55000 },
      { county: 'louth', salary: 45000 },
      { county: 'wicklow', salary: 55000 },
      { county: 'wicklow', salary: 65000 },
      { county: 'donegal', salary: 35000 },
      { county: 'clare', salary: 45000 },
    ],
    []
  );

  const canonicalUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}/affordability`
      : 'https://rentsmart.ie/affordability';

  const shareUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${toPath(selectedSalary, selectedCounty)}`
      : canonicalUrl;
  const shareTitle = `Check rent affordability on ${formatCurrency(selectedSalary)} in ${toTitleCase(
    selectedCounty
  )}`;

  const calc = useMemo(() => {
    const paye = calculatePaye(selectedSalary);
    const usc = calculateUsc(selectedSalary);
    const prsi = calculatePrsi(selectedSalary);
    const totalDeductions = paye + usc + prsi;
    const netAnnual = selectedSalary - totalDeductions;
    const netMonthly = netAnnual / 12;
    const maxRent = Math.round(netMonthly * 0.35);
    const retention = ((netAnnual / selectedSalary) * 100).toFixed(1);

    return {
      paye,
      usc,
      prsi,
      totalDeductions,
      netMonthly,
      maxRent,
      retention,
      buyBudget: Math.round(selectedSalary * 4 * 1.1),
    };
  }, [selectedSalary]);

  return (
    <div className="relative w-screen max-w-none left-1/2 right-1/2 ml-[-50vw] mr-[-50vw] overflow-x-hidden min-h-screen bg-[#f8fafc] font-sans">
      <Helmet>
        <title>How Much Rent Can I Afford in Ireland? (County + Salary, 2026)</title>
        <meta
          name="description"
          content="Check max affordable rent by county and salary in Ireland using 2026 PAYE, USC, PRSI and the 35% net-income rule."
        />
        <link rel="canonical" href={canonicalUrl} />
      </Helmet>

      <header className="bg-slate-900 pt-16 pb-32 px-4 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-emerald-500/10 blur-[120px] rounded-full -mr-20" />
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 px-4 py-1.5 rounded-full text-emerald-400 text-xs font-black uppercase tracking-widest mb-8">
            <ShieldCheck className="h-4 w-4" /> RENTSMART.IE AFFORDABILITY
          </div>
          <h1 className="text-4xl md:text-7xl font-black text-white mb-6 tracking-tight">
            How Much Rent Can You Afford in Dublin and Beyond?
          </h1>
          <p className="text-slate-400 text-lg md:text-xl max-w-3xl mx-auto mb-10 font-medium">
            Localized affordability pages for Irish renters. Built on 2026 tax logic, the 35% net
            rule, county rent medians, and commuter alternatives.
          </p>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 -mt-16 relative z-20 pb-24">
        <div className="bg-white rounded-[2.5rem] shadow-xl shadow-slate-200/60 border border-slate-100 p-6 md:p-10">
          <div className="grid lg:grid-cols-12 gap-12">
            <div className="lg:col-span-5 space-y-8">
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 block">
                  Choose Gross Annual Salary
                </label>
                <select
                  value={selectedSalary}
                  onChange={(event) => setSelectedSalary(Number(event.target.value))}
                  className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 text-2xl font-black text-slate-900 focus:border-emerald-500 focus:bg-white transition-all outline-none"
                >
                  {salaries.map((salary) => (
                    <option key={salary} value={salary}>
                      {formatCurrency(salary)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-3 block">
                  Choose County
                </label>
                <select
                  value={selectedCounty}
                  onChange={(event) => setSelectedCounty(event.target.value)}
                  className="w-full bg-slate-50 border-2 border-slate-100 rounded-2xl px-5 py-4 text-xl font-black text-slate-900 focus:border-emerald-500 focus:bg-white transition-all outline-none"
                >
                  {counties.map((county) => (
                    <option key={county} value={county}>
                      {toTitleCase(county)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="bg-slate-900 rounded-3xl p-8 text-white flex items-center justify-between overflow-hidden relative">
                <div className="relative z-10">
                  <p className="text-emerald-400 text-[10px] font-black uppercase tracking-widest mb-1">
                    Net Income Retention
                  </p>
                  <div className="text-4xl font-black">{calc.retention}%</div>
                  <p className="text-slate-400 text-xs mt-1">after PAYE, USC, PRSI assumptions</p>
                </div>
                <div className="relative h-24 w-24">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="transparent"
                      className="text-slate-800"
                    />
                    <circle
                      cx="48"
                      cy="48"
                      r="40"
                      stroke="currentColor"
                      strokeWidth="8"
                      fill="transparent"
                      strokeDasharray={251.2}
                      strokeDashoffset={251.2 - (251.2 * Number(calc.retention)) / 100}
                      className="text-emerald-500 transition-all duration-1000 ease-out"
                    />
                  </svg>
                  <PieChart className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 h-8 w-8 text-emerald-500" />
                </div>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2 flex items-center justify-between mb-2">
                  <h2 className="text-xl font-black text-slate-900 flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-emerald-500" /> Affordability Snapshot
                  </h2>
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-tighter">
                    Tax Year 2026
                  </span>
                </div>

                <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-6">
                  <p className="text-emerald-600 text-[10px] font-black uppercase tracking-widest mb-1">
                    Net Monthly
                  </p>
                  <div className="text-3xl font-black text-emerald-900">
                    {formatCurrency(calc.netMonthly)}
                  </div>
                </div>

                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-6">
                  <p className="text-slate-500 text-[10px] font-black uppercase tracking-widest mb-1">
                    35% Max Rent
                  </p>
                  <div className="text-3xl font-black text-slate-900">
                    {formatCurrency(calc.maxRent)}
                  </div>
                </div>

                <div className="md:col-span-2 bg-white border border-slate-100 rounded-3xl p-2 space-y-1">
                  {[
                    { label: 'PAYE Income Tax', val: calc.paye },
                    { label: 'USC (Universal Social Charge)', val: calc.usc },
                    { label: 'PRSI (Social Insurance)', val: calc.prsi },
                  ].map((item) => (
                    <div
                      key={item.label}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 rounded-2xl transition-colors"
                    >
                      <span className="font-bold text-slate-600 text-sm">{item.label}</span>
                      <span className="font-black text-slate-900">{formatCurrency(item.val)}</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between p-4 bg-rose-50/50 rounded-2xl mt-2 border border-rose-100/50">
                    <span className="font-black text-rose-700 text-sm uppercase tracking-widest">
                      Total Annual Deductions
                    </span>
                    <span className="font-black text-rose-700 text-xl">
                      {formatCurrency(calc.totalDeductions)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-1 shadow-sm transition-all hover:shadow-md">
            <div className="flex flex-col items-center justify-between gap-6 px-6 py-5 sm:flex-row md:px-8">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                  <Share2 className="h-5 w-5" />
                </div>
                <div className="text-center sm:text-left">
                  <p className="text-[10px] font-black uppercase tracking-[0.1em] text-slate-400 mb-0.5">
                    Share this affordability setup
                  </p>
                  <p className="font-bold leading-tight text-slate-900">
                    {formatCurrency(selectedSalary)} in {toTitleCase(selectedCounty)} with a{' '}
                    {formatCurrency(calc.maxRent)} rent cap.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-2xl bg-slate-50 p-1.5 border border-slate-100">
                <FacebookShareButton url={shareUrl}>
                  <FacebookIcon size={30} round />
                </FacebookShareButton>
                <TwitterShareButton url={shareUrl} title={shareTitle}>
                  <TwitterIcon size={30} round />
                </TwitterShareButton>
                <WhatsappShareButton url={shareUrl} title={shareTitle}>
                  <WhatsappIcon size={30} round />
                </WhatsappShareButton>
                <LinkedinShareButton url={shareUrl} title={shareTitle}>
                  <LinkedinIcon size={30} round />
                </LinkedinShareButton>
              </div>
            </div>
          </div>

          <hr className="my-10 border-slate-100" />

          <div className="grid md:grid-cols-2 gap-6">
            <Link
              to={toPath(selectedSalary, selectedCounty)}
              className="group no-underline relative overflow-hidden bg-gradient-to-br from-emerald-600 to-emerald-800 rounded-[2rem] p-8 text-white transition-all hover:scale-[1.02] hover:shadow-2xl hover:shadow-emerald-900/20"
            >
              <KeyRound className="absolute -right-4 -bottom-4 h-32 w-32 text-white/10 rotate-12" />
              <div className="relative z-10">
                <p className="text-emerald-200 text-xs font-black uppercase tracking-widest mb-2">
                  Detailed County Verdict
                </p>
                <h3 className="text-3xl font-black mb-4">
                  Open {toTitleCase(selectedCounty)} Report
                </h3>
                <p className="text-emerald-100/80 text-sm font-medium mb-8 max-w-xs leading-relaxed">
                  Full net-pay breakdown, reality check against local rent medians, and survival
                  pressure score.
                </p>
                <div className="inline-flex items-center gap-2 bg-white text-emerald-800 px-5 py-3 rounded-xl font-black text-sm uppercase tracking-tighter">
                  Show Report <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            </Link>

            <a
              href={`/browse?listingIntent=rent&location=${encodeURIComponent(
                toTitleCase(selectedCounty)
              )}&maxPrice=${calc.maxRent}`}
              className="group no-underline relative overflow-hidden bg-gradient-to-br from-sky-600 to-sky-800 rounded-[2rem] p-8 text-white transition-all hover:scale-[1.02] hover:shadow-2xl hover:shadow-sky-900/20"
            >
              <Building2 className="absolute -right-4 -bottom-4 h-32 w-32 text-white/10 -rotate-12" />
              <div className="relative z-10">
                <p className="text-sky-200 text-xs font-black uppercase tracking-widest mb-2">
                  Rental Action
                </p>
                <h3 className="text-3xl font-black mb-4">
                  Browse under {formatCurrency(calc.maxRent)}
                </h3>
                <p className="text-sky-100/80 text-sm font-medium mb-8 max-w-xs leading-relaxed">
                  Jump straight into listings aligned with this affordability threshold.
                </p>
                <div className="inline-flex items-center gap-2 bg-white text-sky-800 px-5 py-3 rounded-xl font-black text-sm uppercase tracking-tighter">
                  See Listings <ChevronRight className="h-4 w-4" />
                </div>
              </div>
            </a>
          </div>
        </div>

        <section className="mt-20">
          <div className="flex items-center gap-2 mb-6 px-1">
            <Sparkles className="h-5 w-5 text-emerald-600" />
            <h2 className="text-3xl font-black text-slate-900">Popular County + Salary Pages</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {featured.map((item) => (
              <Link
                key={`${item.salary}-${item.county}`}
                to={toPath(item.salary, item.county)}
                className="group no-underline bg-white border border-slate-200 p-6 rounded-3xl hover:border-emerald-500 hover:shadow-xl hover:-translate-y-1 transition-all"
              >
                <p className="text-[10px] font-black uppercase text-slate-400 tracking-widest mb-1 group-hover:text-emerald-600 transition-colors">
                  Affordability Report
                </p>
                <div className="text-2xl font-black text-slate-900 mb-4">
                  {formatCurrency(item.salary)}
                </div>
                <p className="text-sm font-bold text-slate-500 mb-4">
                  in {toTitleCase(item.county)}
                </p>
                <div className="flex items-center justify-between text-xs font-bold text-slate-500 group-hover:text-slate-900 transition-colors">
                  Open page
                  <ArrowRight className="h-4 w-4 -translate-x-2 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all" />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <footer className="mt-20 bg-slate-100 rounded-[2.5rem] p-8 md:p-12 border border-slate-200/50">
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-500 mb-2">
                <Gauge className="h-4 w-4 text-emerald-600" /> 35% Net Rule
              </div>
              <p className="text-sm font-semibold text-slate-700">
                Rent cap anchored to affordability norms used in Irish housing policy.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-500 mb-2">
                <MapPin className="h-4 w-4 text-sky-600" /> County Reality
              </div>
              <p className="text-sm font-semibold text-slate-700">
                Compare your rent cap against county one-bed medians and commuter pivots.
              </p>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-wider text-slate-500 mb-2">
                <Wallet className="h-4 w-4 text-amber-600" /> Survival Check
              </div>
              <p className="text-sm font-semibold text-slate-700">
                See what is left after rent, utilities and groceries to gauge pressure.
              </p>
            </div>
          </div>

          <div className="flex gap-4">
            <Info className="h-6 w-6 text-slate-400 flex-shrink-0 mt-1" />
            <div>
              <h4 className="text-slate-900 font-black mb-2 uppercase tracking-tighter">
                Important Notice
              </h4>
              <p className="text-sm text-slate-500 leading-relaxed mb-4">
                This calculator provides directional estimates using 2026 PAYE, USC and PRSI
                assumptions and a 35% net affordability benchmark.
              </p>
              <p className="text-sm text-slate-500 font-bold">
                RentSmart.ie is a property research platform and does not provide financial or tax
                advice.
              </p>
            </div>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default RentAffordabilityDiscoverPage;
